/**
 * Detect regex patterns known to cause catastrophic backtracking (ReDoS).
 *
 * We check for the most common structural patterns that lead to exponential
 * time complexity in backtracking regex engines. This is a static heuristic
 * check -- it does not execute the regex.
 */

/**
 * Patterns that indicate nested quantifiers, the primary cause of ReDoS.
 *
 * Examples caught:
 *   (a+)+    -- quantifier on a group containing a quantifier
 *   (a*)*    -- same
 *   (a+)*    -- same
 *   (a{2,})+  -- same with interval quantifiers
 *   (\w+\s+)+ -- nested quantifiers with different atoms
 */
const NESTED_QUANTIFIER_RE = /\([^)]*[+*]\)?[+*{]/;

/**
 * Overlapping alternation with quantifiers.
 *
 * Examples caught:
 *   (a|a)+   -- trivially overlapping alternatives under a quantifier
 *   (\w|\d)+ -- character classes that overlap under a quantifier
 *   (.*|.+)  -- greedy overlapping
 */
const OVERLAPPING_ALTERNATION_RE = /\([^)]*\|[^)]*\)[+*{]/;

/**
 * Backreferences can cause exponential time with certain patterns.
 *
 * Examples caught:
 *   (a+)\1+  -- backreference repeated with quantifier
 */
const DANGEROUS_BACKREF_RE = /\\[1-9]\d*[+*{]/;

/**
 * Check whether a regex pattern string contains structures known to cause
 * catastrophic backtracking (ReDoS).
 *
 * This is intentionally conservative -- it flags patterns that _look_ dangerous
 * based on known structural anti-patterns. False positives are acceptable since
 * the caller will simply fail open and let the server validate instead.
 */
export function isDangerousPattern(pattern: string): boolean {
  if (NESTED_QUANTIFIER_RE.test(pattern)) {
    return true;
  }
  if (OVERLAPPING_ALTERNATION_RE.test(pattern)) {
    return true;
  }
  if (DANGEROUS_BACKREF_RE.test(pattern)) {
    return true;
  }
  return false;
}
